import React, { useCallback, useEffect, useState } from "react";

import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import PremiumValue from "../../components/PremiumValue";
import NoContactChip from "./NoContactChip";

import {
  expirationLabel,
  formatCurrency,
  formatDateOnly,
  hasRealPhone,
  urgencyColor,
} from "./helpers";

const useStyles = makeStyles((theme) => ({
  paper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    ...theme.scrollbarStyles,
  },
  empty: {
    padding: theme.spacing(4),
    textAlign: "center",
    color: theme.palette.text.secondary,
  },
  dot: {
    display: "inline-block",
    width: 8,
    height: 8,
    borderRadius: "50%",
    marginRight: 6,
  },
  rowChip: {
    marginLeft: 6,
  },
}));

/**
 * Listagem tabular de oportunidades — usada pelas visões secundárias do
 * Kanban (fila de reativação e cancelados), onde não há nada a arrastar.
 *
 * `endpoint` e `params` vêm do chamador; a resposta dos dois endpoints segue
 * o mesmo formato { opportunities, count, hasMore }.
 */
const OpportunityTable = ({ endpoint, params, emptyTitle, emptyHint, onOpenTicket, showNextContact }) => {
  const classes = useStyles();

  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [count, setCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);

  const serializedParams = JSON.stringify(params || {});

  const fetchPage = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(endpoint, {
        params: { ...JSON.parse(serializedParams), pageNumber },
      });
      setOpportunities((previous) =>
        pageNumber === 1 ? data.opportunities : [...previous, ...data.opportunities]
      );
      setCount(data.count);
      setHasMore(data.hasMore);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  }, [endpoint, serializedParams, pageNumber]);

  useEffect(() => {
    setPageNumber(1);
  }, [endpoint, serializedParams]);

  useEffect(() => {
    fetchPage();
  }, [fetchPage]);

  if (!loading && opportunities.length === 0) {
    return (
      <Paper className={classes.paper} variant="outlined">
        <div className={classes.empty}>
          <Typography variant="subtitle1" gutterBottom>
            {emptyTitle}
          </Typography>
          <Typography variant="body2">{emptyHint}</Typography>
        </div>
      </Paper>
    );
  }

  return (
    <Paper className={classes.paper} variant="outlined">
      <Typography variant="body2" color="textSecondary" style={{ padding: 8 }}>
        {count} registro(s)
      </Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Cliente</TableCell>
            <TableCell>Produto</TableCell>
            <TableCell>Seguradora</TableCell>
            <TableCell align="center">Vigência até</TableCell>
            {showNextContact ? <TableCell align="center">Voltar a procurar</TableCell> : null}
            <TableCell align="right">Valor Total</TableCell>
            <TableCell align="center">Ações</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {opportunities.map((opportunity) => (
            <TableRow key={opportunity.id} hover>
              <TableCell>
                <span
                  className={classes.dot}
                  style={{ backgroundColor: urgencyColor(opportunity.urgencyBucket) }}
                />
                {opportunity.contact?.name || opportunity.policy?.clientNameRaw}
                {hasRealPhone(opportunity.contact) ? null : (
                  <NoContactChip
                    hasContact={Boolean(opportunity.contact?.id)}
                    className={classes.rowChip}
                  />
                )}
              </TableCell>
              <TableCell>{opportunity.policy?.product}</TableCell>
              <TableCell>{opportunity.policy?.insurer}</TableCell>
              <TableCell align="center">
                {formatDateOnly(opportunity.policy?.endDate)}
                <Typography variant="caption" display="block" color="textSecondary">
                  {expirationLabel(opportunity.daysUntilExpiration)}
                </Typography>
              </TableCell>
              {showNextContact ? (
                <TableCell align="center">{formatDateOnly(opportunity.nextContactDate)}</TableCell>
              ) : null}
              <TableCell align="right">
                <PremiumValue
                  value={opportunity.policy?.premium}
                  source={opportunity.policy?.premiumSource}
                  format={formatCurrency}
                />
              </TableCell>
              <TableCell align="center">
                <Button
                  size="small"
                  color="primary"
                  startIcon={<WhatsAppIcon />}
                  disabled={!hasRealPhone(opportunity.contact)}
                  onClick={() => onOpenTicket(opportunity.contact)}
                >
                  Conversar
                </Button>
              </TableCell>
            </TableRow>
          ))}
          {loading ? <TableRowSkeleton columns={showNextContact ? 7 : 6} /> : null}
        </TableBody>
      </Table>
      {hasMore && !loading ? (
        <div style={{ textAlign: "center", padding: 8 }}>
          <Button onClick={() => setPageNumber((previous) => previous + 1)}>Carregar mais</Button>
        </div>
      ) : null}
    </Paper>
  );
};

export default OpportunityTable;
